import type { FC, ReactNode } from 'react'
import type { Heading } from '@/lib/rich-text'

import { useEffect, useRef, useState } from 'react'
import { Check, Link2, Linkedin, Twitter } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Thin bar under the header that tracks how far through the article the reader is. */
export const ReadingProgress: FC<{ targetId: string }> = ({ targetId }): ReactNode => {
    const barRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const target = document.getElementById(targetId)
        const bar = barRef.current

        if (!target || !bar) return

        let frame = 0

        const update = () => {
            frame = 0

            const rect = target.getBoundingClientRect()
            const total = rect.height - window.innerHeight * 0.6
            const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0

            bar.style.scale = `${progress} 1`
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [targetId])

    return (
        <div aria-hidden='true' className='fixed inset-x-0 top-16 z-40 h-0.5 md:top-[72px]'>
            <span ref={barRef} className='block h-full origin-left scale-x-0 bg-gold-dark' />
        </div>
    )
}

/** Table of contents that highlights the section currently in view. */
export const TableOfContents: FC<{ headings: Array<Heading> }> = ({ headings }): ReactNode => {
    const [active, setActive] = useState<string | null>(null)

    useEffect(() => {
        const elements = headings
            .map(heading => document.getElementById(heading.id))
            .filter((element): element is HTMLElement => element !== null)

        if (!elements.length) return

        const observer = new IntersectionObserver(entries => {
            const visible = entries.filter(entry => entry.isIntersecting)

            if (visible.length) setActive(visible[0].target.id)
        }, { rootMargin: '-20% 0px -70% 0px' })

        elements.forEach(element => observer.observe(element))

        return () => observer.disconnect()
    }, [headings])

    if (!headings.length) return null

    return (
        <nav aria-label='In this article'>
            <p className='font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone'>In this article</p>

            <ol className='mt-4 space-y-1 border-l border-burgundy/10'>
                {headings.map(heading => (
                    <li key={heading.id}>
                        <a
                            href={`#${heading.id}`}
                            aria-current={active === heading.id ? 'location' : undefined}
                            className={cn(
                                '-ml-px block border-l py-1.5 pl-4 text-sm leading-snug transition-colors duration-300',
                                active === heading.id ? 'border-burgundy text-burgundy' : 'border-transparent text-stone-dark hover:text-burgundy'
                            )}
                        >
                            {heading.text}
                        </a>
                    </li>
                ))}
            </ol>
        </nav>
    )
}

/** Copy link and share buttons. The page address is read after mount. */
export const ShareLinks: FC<{ title: string }> = ({ title }): ReactNode => {
    const [url, setUrl] = useState('')
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        setUrl(window.location.href.split('#')[0])
    }, [])

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 2000)

        return () => window.clearTimeout(timer)
    }, [copied])

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url || window.location.href)
            setCopied(true)
        }
        catch {
            setCopied(false)
        }
    }

    const encodedUrl = encodeURIComponent(url)
    const encodedTitle = encodeURIComponent(title)
    const button = 'flex size-10 items-center justify-center rounded-full border border-burgundy/15 text-burgundy transition-colors duration-300 hover:border-burgundy/40 hover:bg-ivory'

    return (
        <div>
            <p className='font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone'>Share</p>

            <div className='mt-4 flex items-center gap-2'>
                <button type='button' onClick={copy} className={button} aria-label={copied ? 'Link copied' : 'Copy link'}>
                    {copied ? <Check aria-hidden='true' className='size-4 text-positive' /> : <Link2 aria-hidden='true' className='size-4' />}
                </button>

                <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label='Share on LinkedIn'
                    className={button}
                >
                    <Linkedin aria-hidden='true' className='size-4' />
                </a>

                <a
                    href={`https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label='Share on X'
                    className={button}
                >
                    <Twitter aria-hidden='true' className='size-4' />
                </a>

                <span role='status' className='sr-only'>{copied ? 'Link copied to clipboard' : ''}</span>
            </div>
        </div>
    )
}
